import React from "react";
import {
  Button,
  Col,
  Modal,
  Row,
  Form as BForm,
  OverlayTrigger,
  Tooltip,
} from "@themesberg/react-bootstrap";
import { ErrorMessage, Form, Formik } from "formik";
import Toggle from "./Toggle";
import { createPromoCodeSchema } from "../schema.js";
import { promoCodeHeaders } from "../constants.js";
import { formatDateYMD } from "../../../../utils/dateFormatter.js";
import { isDateValid } from "../constants.js"

const renderTooltip = (props) => (
  <Tooltip id="submit-tooltip" {...props}>
    Cannot submit: Validity date has expired
  </Tooltip>
);

const voucherFieldTooltip = (props, message) => (
  <Tooltip id="voucher-field-tooltip" {...props}>
    {message}
  </Tooltip>
);

const AddEditModal = ({ type, data, show, setShow, createUpdate, entityName = "Promo Code", isVoucherTab = false, loading = false, currentConversionRate = null }) => {
  // Map voucher code fields to common field names used in the form
  const getInitialValues = () => {
    if (isVoucherTab) {
      // Voucher code field mapping - amount is stored and edited in TTD
      const statusBool = data?.status === "active" || data?.status === true;
      return {
        voucherCodeId: data?.voucherCodeId || 0,
        promocodesId: data?.voucherCodeId || 0, // For compatibility
        code: data?.code || "",
        amount: data?.amountTtd ?? data?.amountUsd ?? data?.amount ?? "",
        amountTtd: data?.amountTtd || "",
        validity: data?.expiresAt
          ? new Date(data.expiresAt).toISOString().split('T')[0]
          : (data?.validity ? new Date(data.validity).toISOString().split('T')[0] : ""),
        status: statusBool,
        notes: data?.notes || "",
      };
    } else {
      // Promo code field mapping
      return {
        promocodesId: data?.promocodesId || 0,
        code: data?.code || "",
        amount: data?.amount || "",
        validity: data?.validity ? new Date(data.validity).toISOString().split('T')[0] : "",
        status: data?.status !== undefined ? data.status : true,
      };
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{type} {entityName}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={getInitialValues()}
            validationSchema={createPromoCodeSchema}
            onSubmit={(formValues, { setSubmitting }) => {
              // Convert validity back to ISO string for submission
              const submitData = {
                ...formValues,
                code: formValues?.code?.toUpperCase() || "",
                validity: formValues.validity ? formatDateYMD(new Date(formValues.validity)) : null,
                amount: parseFloat(formValues.amount)
              };
              createUpdate(submitData);
              // We rely on external redux `loading` state for the submit button.
              // Ensure Formik's internal submitting state is reset immediately.
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              handleSubmit,
              handleBlur,
              handleChange,
              setFieldValue,
              isValid,
              dirty,
              initialValues,
              touched,
              isSubmitting
            }) => {
              // Custom validation check for submit button
              const hasRequiredFields = values.code && values.amount && values.validity;
              const hasNoErrors = Object.keys(errors).length === 0;
              const isFormValid = hasRequiredFields && hasNoErrors;

              // For edit mode, check if form is dirty OR if it's create mode
              const canSubmit = type === "Create" ? isFormValid : (isFormValid && dirty);

              // In voucher edit mode, code is read-only
              const isVoucherEditMode = isVoucherTab && type === "Edit";

              return (
                <Form>
                  {promoCodeHeaders
                    .filter((h) => h.isShowModal)
                    .slice(0, promoCodeHeaders.length - 1)
                    .map((h) => (
                      <Row className="mt-3" key={h.value}>

                        <Col className="d-flex align-items-center">
                          <Col>
                            <BForm.Label>
                              {/* For vouchers, amount is in TTD */}
                              {isVoucherTab && h.value === "amount" ? "Amount (TTD)" : h.label}
                              {h.required && (
                                <span className="text-danger">*</span>
                              )}
                            </BForm.Label>
                          </Col>

                          <Col xs={9}>
                            {h.value === "status" ? (
                              <Toggle
                                checked={values.status}
                                onChange={(e) => {
                                  setFieldValue("status", e.target.checked);
                                }}
                                justify="left"
                              />
                            ) : h.value === "validity" ? (
                              <BForm.Control
                                type="date"
                                name={h.value}
                                placeholder={isVoucherTab && h.value === "amount" ? "Amount (TTD)" : h.label}
                                value={values[h.value]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                min={new Date().toISOString().split('T')[0]}
                                isInvalid={touched[h.value] && !!errors[h.value]}
                              />
                            ) : h.value === "amount" ? (
                              isVoucherEditMode ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={(props) =>
                                    voucherFieldTooltip(props, "Amount cannot be changed when editing a voucher.")
                                  }
                                >
                                  <span className="d-inline-block w-100">
                                    <BForm.Control
                                      type="number"
                                      name={h.value}
                                      placeholder={isVoucherTab ? "Amount (TTD)" : h.label}
                                      value={values[h.value]}
                                      style={{
                                        cursor: "not-allowed",
                                        backgroundColor: "var(--bs-secondary-bg)",
                                      }}
                                      readOnly
                                      isInvalid={touched[h.value] && !!errors[h.value]}
                                    />
                                  </span>
                                </OverlayTrigger>
                              ) : (
                                <BForm.Control
                                  type="number"
                                  name={h.value}
                                  placeholder={isVoucherTab ? "Amount (TTD)" : h.label}
                                  value={values[h.value]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  min="0"
                                  step="0.01"
                                  isInvalid={touched[h.value] && !!errors[h.value]}
                                />
                              )
                            ) : h.value === "code" ? (
                              isVoucherEditMode ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={(props) => voucherFieldTooltip(props, "Code cannot be changed when editing a voucher.")}
                                >
                                  <span className="d-inline-block">
                                    <BForm.Control
                                      type="text"
                                      name={h.value}
                                      placeholder={h.label}
                                      value={values[h.value]}
                                      style={{ textTransform: 'uppercase', cursor: "not-allowed", backgroundColor: "var(--bs-secondary-bg)" }}
                                      readOnly
                                      isInvalid={touched[h.value] && !!errors[h.value]}
                                    />
                                  </span>
                                </OverlayTrigger>
                              ) : (
                                <BForm.Control
                                  type="text"
                                  name={h.value}
                                  placeholder={h.label}
                                  value={values[h.value]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  style={{ textTransform: 'uppercase' }}
                                  isInvalid={touched[h.value] && !!errors[h.value]}
                                />
                              )
                            ) : (
                              <BForm.Control
                                type={
                                  h.inputType === "textarea"
                                    ? "text"
                                    : h.inputType || "text"
                                }
                                name={h.value}
                                placeholder={h.label}
                                value={values[h.value]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched[h.value] && !!errors[h.value]}
                                {...(h.inputType === "textarea" && {
                                  as: "textarea",
                                  rows: 5,
                                  style: {
                                    resize: "vertical",
                                    minHeight: "80px",
                                    maxHeight: "200px",
                                  },
                                })}
                              />
                            )}

                            <ErrorMessage
                              component="div"
                              name={h.value}
                              className="text-danger"
                            />
                          </Col>
                        </Col>
                      </Row>
                    ))}

                  <div className="mt-4 d-flex justify-content-between align-items-center">
                    <Button
                      className="btn-danger"
                      onClick={() => setShow(false)}
                    >
                      Cancel
                    </Button>
                    {type !== "Create" && !isDateValid(values?.validity) ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip}
                      >
                        <div className="d-inline-block" style={{ cursor: 'not-allowed' }}>
                          <Button
                            className="btn-primary ml-2"
                            disabled={!canSubmit || loading}
                            type="submit"
                            onClick={handleSubmit}
                            style={{ pointerEvents: 'none' }}
                          >
                            {loading ? "Submitting..." : "Submit"}
                          </Button>
                        </div>
                      </OverlayTrigger>
                    ) : (
                      <div className="d-inline-block">
                        <Button
                          className="btn-primary ml-2"
                          disabled={!canSubmit || loading}
                          type="submit"
                          onClick={handleSubmit}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </Button>
                      </div>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddEditModal;
